import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ElevatingProfessionalStandards() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const features = [{
    text: "30+ Years Experience"
  }, {
    text: "Local Central Coast Expert"
  }, {
    text: "Premium Coastal Finishes"
  }];
  return <section data-section-id="38963" id="northern-central-coast-painting-excellence" ref={sectionRef} className="relative w-full overflow-hidden bg-white font-sans selection:bg-accent/30">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
        <div className="grid min-h-[85vh] grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          {}
          <div className="relative z-10 py-12 lg:py-24">
            <div className={`transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-secondary/10 px-4 py-1.5 text-sm font-semibold text-primary">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-accent"></span>
                </span>
                Serving the Northern Central Coast
              </div>

              <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-primary sm:text-5xl md:text-6xl lg:text-7xl">
                Quality Painting on the <br />
                <span className="text-secondary">Central Coast</span>
              </h1>

              <p className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                Combining 30 years of local expertise with premium architectural finishes. 
                From Gorokan to Toukley, we transform residential and industrial spaces with 
                meticulous attention to detail.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {features.map((feature, i) => <div key={i} data-index={i} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-accent" />
                    <span className="text-base font-medium text-primary/80">{feature.text}</span>
                  </div>)}
              </div>

              <div className="mt-12 flex flex-wrap gap-4">
                <Button asChild size="lg" className="h-14 bg-accent px-8 text-base font-bold text-accent-foreground shadow-lg transition-all hover:scale-105 hover:bg-accent/90 hover:shadow-accent/20">
                  <Link to="/ContactPage">
                    Get a Free Quote <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-14 border-primary/20 px-8 text-base font-bold text-primary hover:bg-secondary/5">
                  <Link to="/ServicesPage">Our Services</Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className={`relative flex items-center justify-center transition-all duration-1000 delay-300 ease-out ${isVisible ? "translate-x-0 opacity-100" : "translate-x-20 opacity-0"}`}>
            <div className="relative h-[500px] w-full max-w-[600px] lg:h-[700px]">
              {}
              <div className="absolute -right-20 -top-20 h-64 w-64 animate-pulse rounded-full bg-secondary/10 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-accent/5 blur-3xl" />
              
              {}
              <div className="relative h-full w-full overflow-hidden rounded-[40px] rounded-tr-[120px] bg-muted shadow-2xl">
                <img src="http://clint.websiteshop.au/wp-content/uploads/2026/03/Painted-House-and-Pool.png" alt="Premium coastal residence with professional exterior painting" className="h-full w-full object-cover transition-transform duration-[3s] hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
                
                {}
                <div className="absolute bottom-8 left-8 right-8 rounded-2xl bg-white/95 p-6 shadow-xl backdrop-blur-md">
                  <div className="flex items-center gap-4">
                    
                    <div>
                      <p className="text-sm font-bold text-primary">30+ years of experience</p>
                      <p className="text-xs text-muted-foreground">Trusted by local homeowners since 1994</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]">
        <svg className="relative block h-[40px] w-[calc(100%+1.3px)] lg:h-[80px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-muted/50"></path>
        </svg>
      </div>
    </section>;
}